import type { AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styled from 'styled-components';

import { createShouldForwardStyledProp } from '../../../styles';
import { Icon } from '../../Icons';
import type { IconName } from '../../Icons/IconNames';
import { iconButtonStyles } from '../IconButton/styles';
import { buttonStyle, type ButtonProps } from '../styles';

type NavIconButtonBaseProps = Omit<
  ButtonProps<AnchorHTMLAttributes<HTMLAnchorElement>>,
  'color' | 'done' | 'endIcon' | 'flex' | 'justifyContent' | 'loading' | 'pill' | 'prefix' | 'startIcon' | 'suffix'
>;

export interface NavIconButtonWrapperProps extends NavIconButtonBaseProps, Omit<LinkProps, 'to'> {
  /** If target is round or square */
  round?: boolean;
  to?: LinkProps['to'];
}

// styled(Link) forwards props through a custom component, so filter the
// style-only props consumed by buttonStyle/iconButtonStyles before they reach Link/the DOM.
const nonForwardedProps = new Set([
  'dim',
  'disabled',
  'ghost',
  'height',
  'prominent',
  'round',
  'size',
  'variant',
  'width',
]);

export const NavIconButtonWrapper = styled(Link).withConfig({
  shouldForwardProp: createShouldForwardStyledProp(nonForwardedProps),
})<NavIconButtonWrapperProps>`
  text-decoration: none;

  ${({ theme, size, variant, disabled, ghost, dim, prominent }) =>
    buttonStyle({
      theme,
      size,
      variant,
      disabled,
      ghost,
      prominent,
      dim,
    })}

  ${iconButtonStyles}
`;

export interface NavIconButtonProps extends NavIconButtonWrapperProps {
  icon: IconName;
  color?: string;
}

export const NavIconButton = styled(({ icon, variant, color, ghost, to, ...props }: NavIconButtonProps) => (
  <NavIconButtonWrapper
    to={props.disabled ? '#' : (to as string)}
    variant={ghost ? undefined : variant}
    ghost={ghost}
    {...props}
  >
    <Icon icon={icon} variant={ghost ? variant : undefined} color={color} />
  </NavIconButtonWrapper>
))``;
